import React, { Component } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from "reactstrap";

import { UploadFile } from "./utils/DataFetch";
import { GetCookie } from "./utils/Common";

import "./Translation.css";

/**
 * History types offered for translations, as { value stored in JobHistory, label shown }.
 * Work-order types that update the job through the work-order pipeline (Authorization,
 * To Booking Dept, Provider Conf, ...) are left out: translations have no claimant, so
 * that pipeline cannot find them and those saves would fail.
 */
export const TRANSLATION_HISTORY_TYPES = [
  { value: "Job Note", label: "Note" },
  { value: "Attachment", label: "Attachment" },
  { value: "Interpreter Attachment", label: "Translator attachment" },
  { value: "Rep Email", label: "Rep email" },
  { value: "Int Communication", label: "Translator communication" },
  { value: "Special Billing", label: "Special billing" },
];

export const historyTypeLabel = (value) =>
  TRANSLATION_HISTORY_TYPES.find((t) => t.value === value)?.label ?? value;

const isAttachmentType = (value) =>
  value === "Attachment" || value === "Interpreter Attachment";

/** Adds or edits one Translation History entry (a JobHistory row), optionally with files. */
export class TranslationHistoryForm extends Component {
  static displayName = TranslationHistoryForm.name;

  constructor(props) {
    super(props);
    this.user = JSON.parse(GetCookie("user"));
    this.state = {
      model: props.model,
      files: [],
      alert: { color: "warning", message: "", isOpen: false },
      saving: false,
    };
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({ model: { ...prev.model, [name]: value } }));
  };

  handleFiles = (e) => {
    this.setState({ files: Array.from(e.target.files ?? []) });
  };

  onSave = () => {
    const { model, files } = this.state;
    const isInsert = model.EventType === "insert";

    if (isAttachmentType(model.JobHistoryType) && isInsert && files.length === 0) {
      this.setState({ alert: { color: "warning", message: "Choose a file to attach.", isOpen: true } });
      return;
    }
    if (!isAttachmentType(model.JobHistoryType) && !model.Description?.trim()) {
      this.setState({ alert: { color: "warning", message: "Enter the note text.", isOpen: true } });
      return;
    }

    const formData = new FormData();
    formData.append("EventType", model.EventType);
    formData.append("JobHistoryId", model.JobHistoryId ?? "");
    formData.append("JobId", model.JobId);
    formData.append("JobHistoryName", "");
    formData.append("Description", model.Description ?? "");
    formData.append("Contact", "");
    formData.append("ContactEmail", "");
    formData.append("JobHistoryType", model.JobHistoryType);
    formData.append("CreateDateTime", model.CreateDateTime ?? "");
    // The history list joins on the creator, so every entry must carry one.
    formData.append("CreateByUserId", model.CreateByUserId ?? this.user.userid);
    if (files.length > 0) {
      formData.append("FileName", files.map((f) => f.name).join(","));
      files.forEach((f) => formData.append("FormFile", f));
    } else {
      formData.append("FileName", model.FileName ?? "");
      formData.append("FileLocation", model.FileLocation ?? "");
    }

    this.setState({ saving: true });
    UploadFile("/savejobhistory", this.user.token, formData).then((data) => {
      this.setState({ saving: false });
      if (data === "Success") {
        this.props.onSaved(isInsert ? "History entry added." : "History entry updated.");
      } else {
        this.setState({
          alert: { color: "warning", message: data || "Could not save the history entry.", isOpen: true },
        });
      }
    });
  };

  render() {
    const { model, files, alert, saving } = this.state;
    const isInsert = model.EventType === "insert";
    const attachment = isAttachmentType(model.JobHistoryType);
    const editable = model.IsEditableFlg !== false;

    return (
      <Modal isOpen toggle={this.props.onCancel} modalClassName="tr-modal" backdrop="static">
        <ModalHeader toggle={this.props.onCancel}>
          <span className="tr-modal-title">
            {isInsert ? "Add to Translation History" : "Edit history entry"}
          </span>
        </ModalHeader>
        <ModalBody>
          <Alert color={alert.color} isOpen={alert.isOpen} role="alert">
            {alert.message}
          </Alert>
          <fieldset disabled={!editable}>
            <section className="tr-section">
              <div className="mb-3">
                <label className="form-label" htmlFor="tr-hist-type">
                  Type
                </label>
                <select
                  id="tr-hist-type"
                  name="JobHistoryType"
                  className="form-select"
                  value={model.JobHistoryType}
                  onChange={this.handleChange}
                  disabled={!isInsert}
                >
                  {TRANSLATION_HISTORY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="tr-hist-desc">
                  {attachment ? "Comment" : "Note"}
                </label>
                <textarea
                  id="tr-hist-desc"
                  name="Description"
                  rows={4}
                  className="form-control"
                  value={model.Description ?? ""}
                  onChange={this.handleChange}
                  autoFocus
                />
              </div>
              {attachment && (
                <div>
                  <label className="form-label" htmlFor="tr-hist-files">
                    {isInsert ? "Files" : "File"}
                  </label>
                  {isInsert ? (
                    <input
                      id="tr-hist-files"
                      type="file"
                      multiple
                      className="form-control"
                      onChange={this.handleFiles}
                      aria-describedby="tr-hist-files-hint"
                    />
                  ) : (
                    <div className="tr-selected-chip">{model.FileName || "No file"}</div>
                  )}
                  <p id="tr-hist-files-hint" className="tr-hint">
                    {isInsert
                      ? files.length > 1
                        ? `${files.length} files selected; each is saved as its own entry with this comment.`
                        : "You can select more than one file."
                      : "To replace a file, add a new attachment and delete this one."}
                  </p>
                </div>
              )}
            </section>
          </fieldset>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={this.props.onCancel} disabled={saving}>
            Cancel
          </Button>
          {editable && (
            <Button color="primary" onClick={this.onSave} disabled={saving}>
              {saving ? "Saving…" : isInsert ? "Add entry" : "Save changes"}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    );
  }
}
